import { Box } from '@mui/material';
import { Container, Heading, Section, Text } from '../components/atomic';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * Terms of Use page component for RSM Collective
 *
 * Comprehensive terms of service covering website usage, user responsibilities,
 * and legal agreements between RSM Collective and users.
 *
 * @returns {JSX.Element} Terms of Use page component
 */
function TermsOfUse() {
  return (
    <LegalPageLayout>
      <Section
        sx={{
          backgroundColor: 'var(--color-background-light)',
          py: 0,
          position: 'relative',
        }}
      >
        {/* Green Bar Header Treatment */}
        <Box
          sx={{
            borderTop: '4px solid var(--color-primary)',
            borderBottom: '4px solid var(--color-primary)',
            backgroundColor:
              'color-mix(in srgb, var(--color-primary) 5%, transparent)',
            py: 4,
            mb: 6,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center' }}>
              <Heading
                variant="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Terms of Use
              </Heading>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {/* Introduction */}
            <Box sx={{ mb: 6 }}>
              <Text sx={{ fontSize: '1.125rem', lineHeight: 1.7, mb: 3 }}>
                Welcome to RSM Collective. These Terms of Use ("Terms") govern
                your use of our website, AWS Marketplace consulting services,
                and any related content. By accessing or using our website, you
                agree to be bound by these Terms.
              </Text>
            </Box>

            {/* Acceptance of Terms */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Acceptance of Terms
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                By accessing and using this website, you accept and agree to be
                bound by the terms and provision of this agreement. If you do
                not agree to abide by the above, please do not use this service.
              </Text>
            </Box>

            {/* Use License */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Use License
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Permission is granted to temporarily download one copy of the
                materials on RSM Collective's website for personal,
                non-commercial transitory viewing only. This is the grant of a
                license, not a transfer of title, and under this license you may
                not:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Modify or copy the materials
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Use the materials for any commercial purpose or for any public
                  display
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Attempt to reverse engineer any software contained on the
                  website
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Remove any copyright or other proprietary notations from the
                  materials
                </Text>
              </Box>
            </Box>

            {/* User Accounts */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                User Accounts
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                When you create an account with us, you must provide information
                that is accurate, complete, and current at all times. You are
                responsible for safeguarding the password and for maintaining
                the confidentiality of your account.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                You agree not to disclose your password to any third party and
                to take sole responsibility for any activities or actions under
                your account, whether or not you have authorized such
                activities.
              </Text>
            </Box>

            {/* Prohibited Uses */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Prohibited Uses
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                You may not use our service:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  For any unlawful purpose or to solicit others to perform
                  unlawful acts
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To violate any international, federal, provincial, or state
                  regulations, rules, laws, or local ordinances
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To infringe upon or violate our intellectual property rights
                  or the intellectual property rights of others
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To harass, abuse, insult, harm, defame, slander, disparage,
                  intimidate, or discriminate
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To submit false or misleading information
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To upload or transmit viruses or any other type of malicious
                  code
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To spam, phish, pharm, pretext, spider, crawl, or scrape
                </Text>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Content
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Our service allows you to post, link, store, share and otherwise
                make available certain information, text, graphics, videos, or
                other material. You are responsible for the content that you
                post to the service, including its legality, reliability, and
                appropriateness.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                By posting content to the service, you grant us the right and
                license to use, modify, publicly perform, publicly display,
                reproduce, and distribute such content on and through the
                service.
              </Text>
            </Box>

            {/* Intellectual Property Rights */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Intellectual Property Rights
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The service and its original content, features, and
                functionality are and will remain the exclusive property of RSM
                Collective and its licensors. The service is protected by
                copyright, trademark, and other laws.
              </Text>
            </Box>

            {/* Termination */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Termination
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We may terminate or suspend your account and bar access to the
                service immediately, without prior notice or liability, under
                our sole discretion, for any reason whatsoever and without
                limitation, including but not limited to a breach of the Terms.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you wish to terminate your account, you may simply
                discontinue using the service.
              </Text>
            </Box>

            {/* Disclaimer */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Disclaimer
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The information on this website is provided on an "as is" basis.
                To the fullest extent permitted by law, this Company excludes
                all representations, warranties, conditions and terms (express
                or implied, statutory or otherwise) other than those expressly
                set out in the Terms and Conditions.
              </Text>
            </Box>

            {/* Limitation of Liability */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Limitation of Liability
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                In no event shall RSM Collective, nor its directors, employees,
                partners, agents, suppliers, or affiliates, be liable for any
                indirect, incidental, special, consequential, or punitive
                damages, including without limitation, loss of profits, data,
                use, goodwill, or other intangible losses, resulting from your
                use of the service.
              </Text>
            </Box>

            {/* Governing Law */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Governing Law
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                These Terms shall be interpreted and governed by the laws of the
                State of California, without regard to conflict of law
                provisions. Our failure to enforce any right or provision of
                these Terms will not be considered a waiver of those rights.
              </Text>
            </Box>

            {/* Changes to Terms */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Changes to Terms
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We reserve the right, at our sole discretion, to modify or
                replace these Terms at any time. If a revision is material, we
                will provide at least 30 days notice prior to any new terms
                taking effect.
              </Text>
            </Box>

            {/* Contact Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Contact Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you have any questions about these Terms of Use, please
                contact us at:
              </Text>
              <Text sx={{ mb: 2, lineHeight: 1.7, fontWeight: 500 }}>
                RSM Collective
                <br />
                Email:{' '}
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                />
                <br />
                Address: 401 West A St. Ste. 200, San Diego, CA 92101
              </Text>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default TermsOfUse;
